import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthSync } from '../../hooks/useAuthSync';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'tenant' | 'landlord' | 'agent';
  fallbackPath?: string;
}

export default function ProtectedRoute({ 
  children, 
  requiredRole, 
  fallbackPath = '/signin' 
}: ProtectedRouteProps) {
  const { user, loading } = useAuthSync();
  const location = useLocation();
  
  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
      </div>
    );
  }

  // Redirect to signin if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Check role if required
  if (requiredRole && user?.user_metadata?.user_type !== requiredRole) {
    // Redirect to unauthorized page for role mismatch
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
